import { Heading, Text } from "@solomon-labs/design/components/ui";
import React, { useCallback, useMemo, useState } from "react";

import { List } from "../../../components/Data/List";
import { Section } from "../../../components/Section";
import { StatusBadge } from "../../../components/StatusBadge/StatusBadge";
import { Button } from "../../../components/ui/button";
import { FormActions } from "../../../components/ui/form-actions";
import { useSimCursor } from "../../sim-cursor/SimCursor";

import { framable, type FramableProps } from "./framable";
import {
    NB_A_BALANCE_TRANCHE_VALUE,
    NB_A_BALANCE_TRANCHES,
    NB_A_CONFIG,
    NB_A_MINIMUM_THRESHOLD_VALUE,
    NB_A_PROGRAM_NAME,
    NB_BALANCE_AMOUNT_TRANCHE,
    NB_MINIMUM_THRESHOLD,
    NB_WALLET_LIST
} from "./neobankFixtures";
import { PolicyCreator, type PolicyCreatorDemo } from "./PolicyCreator";
import {
    initialValueFor,
    POLICY_CATALOG,
    serializeTranches,
    serializeWalletConfig
} from "./policyFixtures";
import { PolicyRow } from "./PolicyRow";
import {
    type ProgramConfigControl,
    type ProgramConfigDemo,
    ProgramConfiguration
} from "./ProgramConfiguration";
import type { ConfigurablePolicy, Policy, ProgramConfig } from "./types";
import {
    editNumberFieldBeats,
    type SimScriptStep,
    typedNumberBeats,
    useSimScript
} from "./useSimScript";

/**
 * Neobank scenario — **Sim A: "Continuous Balance Rewards"**.
 *
 * A single, looping, cursor-driven walkthrough of the neobank configuring an
 * ongoing USDv-holding rewards program in PMP, then activating it. It stitches
 * the same fixture-driven, props-only program-builder Views the other doc
 * blocks use ({@link ProgramConfiguration}, {@link ProgramPolicies}'s parts,
 * {@link FormActions}) under one {@link useSimCursor}, and auto-plays a
 * believable "someone building the program" script with {@link useSimScript}:
 *
 *   Phase 1 — Configuration: cursor picks **Continuous** program type (no fixed
 *     period — the Period row reads "Continuous"), sets **Distribution Schedule
 *     = 14 days** (earned/variable budget, so no fixed budget field).
 *   Phase 2 — Policies: cursor builds **Minimum Threshold 100 USDv** (hold-to-
 *     qualify), then a **Balance Amount Tranche** (>1k / >10k / >50k earn more),
 *     then **Specific Access Control** (KYC Only mode — the KYC-approved
 *     requirement).
 *   Phase 3 — Activation: cursor clicks **Add Program**; then the ENTIRE sim
 *     (builder view + cursor) FADES OUT to opacity 0, holds faded for ~3s, and
 *     the loop reset fades it back in and replays from the empty draft. No read
 *     view, no Pending → Active flip, no jump to the Programs rail.
 *
 * No wallet / RPC / orgApiFetch — pure fixtures; reduced-motion collapses to
 * the fully-built draft end-state (useSimScript's synchronous pass).
 *
 * NEOBANK-CASE MAPPING + GAPS (see neobankFixtures for the full table):
 *  - Distribution every 14 days → Distribution Schedule = 14 days.  ✅ real
 *  - Hold ≥100 USDv → Minimum Threshold.                            ✅ real
 *  - Balance tiers earn more → Balance Amount Tranche.              ✅ real
 *  - KYC-approved requirement → Specific Access Control · KYC Only mode. ✅ real
 *  - 80/20 customer/neobank split → Configuration User % = 80.      ✅ real
 */

const CONTINUOUS_PROGRAM_FIXTURE: ProgramConfig = {
    status: "in_progress",
    period: "continuous"
};

interface ConfigDraft {
    programType: "fixed" | "continuous";
    budgetMode: "fixed" | "open";
    apyMode: "earned" | "base";
    baseApy: number;
    // `null` = the field is CLEARED mid-edit (renders blank) — the User % edit
    // opens at the default 100, clears to blank, then types down to 80.
    userPct: number | null;
    periodDays: number;
    settlementCycleLength: number;
    settlementCycleUnit: "days" | "weeks";
    budget: number;
}

const EMPTY_DRAFT: ConfigDraft = {
    // Opens on the fresh-draft DEFAULTS: Continuous · Variable (earned) ·
    // Distribution 14. The walkthrough no longer clicks Program Type —
    // Continuous is pre-selected, the Budget Type already reads Variable, the
    // User % field is visible, and the Distribution Schedule already reads 14
    // days (the neobank's cadence == the default, so it's NOT re-typed).
    programType: "continuous",
    budgetMode: "open",
    apyMode: "earned",
    baseApy: 0,
    // Demoed — OPENS at the real DEFAULT (100), then the cursor edits it DOWN
    // to the 80% split: focus → clear the 100 (blank) → type 8 → 80 (a
    // realistic "change the default" edit, not a type-from-empty).
    userPct: 100,
    // Default (hidden while Continuous), kept at its real value.
    periodDays: 90,
    // The neobank distributes every 14 days — which is exactly the default, so
    // the field opens already reading 14 and there's no typing beat for it.
    settlementCycleLength: 14,
    settlementCycleUnit: "days",
    // Default budget (Fixed-Budget path only — not used here).
    budget: 100000
};

interface AttachedPolicy {
    policy: Policy;
    value: number | string;
}

interface PoliciesFixture {
    attached: AttachedPolicy[];
    editorType: ConfigurablePolicy | null;
    editorValue: number | string;
}

const EMPTY_POLICIES: PoliciesFixture = {
    attached: [],
    editorType: null,
    editorValue: ""
};

const CFG_DEMO_IDLE: ProgramConfigDemo = { control: null, phase: "hover" };
const POL_DEMO_IDLE: PolicyCreatorDemo = { control: null, phase: "hover" };

// ── Beat timing (ms) ─────────────────────────────────────────────────────
const TRAVEL_MS = 560;
const HOVER_HOLD_MS = 440;
const PRESS_HOLD_MS = 360;
const OPTION_STEP_MS = 200;
/** Duration of the opacity fade-out after Add Program (and the fade-in on
 *  replay). Matches the `duration-500` Tailwind utility on the root wrapper. */
const FADE_MS = 500;
/** Hold the sim FADED OUT (opacity 0) before the loop reset fades it back in
 *  and replays — the requested ~3s dwell. */
const FADE_HOLD_MS = 3000;

export interface NeobankContinuousProgramSimProps extends FramableProps {
    /** Pause (ms) after the faded-out hold before replaying from the empty
     *  draft. Defaults to 0 (the ~3s dwell lives in the faded-out hold beat).
     *  `-1` = play once and stop. */
    loopPauseMs?: number;
}

function NeobankContinuousProgramSimView({
    loopPauseMs = 0
}: NeobankContinuousProgramSimProps = {}): React.ReactElement {
    // `faded` drives the root wrapper's opacity: false = opacity 1 (visible),
    // true = opacity 0 (faded out after Add Program). Reset flips it back to
    // false on replay → CSS transition fades the rebuilt sim back in.
    const [faded, setFaded] = useState(false);
    const [draft, setDraft] = useState<ConfigDraft>(EMPTY_DRAFT);
    const [cfgDemo, setCfgDemo] = useState<ProgramConfigDemo>(CFG_DEMO_IDLE);
    const [policies, setPolicies] = useState<PoliciesFixture>(EMPTY_POLICIES);
    const [createHover, setCreateHover] = useState<"hover" | "press" | null>(
        null
    );
    const [polDemo, setPolDemo] = useState<PolicyCreatorDemo>(POL_DEMO_IDLE);
    const [ctaDemo, setCtaDemo] = useState<"hover" | "press" | null>(null);

    const cursor = useSimCursor();
    const {
        moveTo: moveCursor,
        click: clickCursor,
        hide: hideCursor,
        containerRef
    } = cursor;

    const patchDraft = useCallback((next: Partial<ConfigDraft>): void => {
        setDraft((d) => ({ ...d, ...next }));
    }, []);
    const patchPolicies = useCallback(
        (next: Partial<PoliciesFixture>): void => {
            setPolicies((p) => ({ ...p, ...next }));
        },
        []
    );

    const reset = useCallback((): void => {
        setFaded(false);
        setDraft(EMPTY_DRAFT);
        setCfgDemo(CFG_DEMO_IDLE);
        setPolicies(EMPTY_POLICIES);
        setCreateHover(null);
        setPolDemo(POL_DEMO_IDLE);
        setCtaDemo(null);
        hideCursor();
    }, [hideCursor]);

    const openEditor = useCallback(
        (policy: ConfigurablePolicy, value: number | string): void => {
            setPolicies((p) => ({
                ...p,
                editorType: policy,
                editorValue: value
            }));
        },
        []
    );
    const commitEditor = useCallback(
        (policy: ConfigurablePolicy, value: number | string): void => {
            setPolicies((p) => ({
                attached: [...p.attached, { policy, value }],
                editorType: null,
                editorValue: ""
            }));
        },
        []
    );

    // A numeric cfg FIELD the cursor EDITS a PRE-FILLED value down/up on
    // (User %): the field opens already reading its current value (the default),
    // then it clears and the new value types in (e.g. 100 → blank → 8 → 80).
    // Thin wrapper over the shared `editNumberFieldBeats` — supplies this sim's
    // cursor/demo/patch closures + timing so all three program-builder sims
    // share the one "seed the default, then demo the edit" helper.
    const editCfgBeats = useCallback(
        (
            control: ProgramConfigControl,
            cursorKey: string,
            key: keyof ConfigDraft,
            target: number
        ): SimScriptStep[] =>
            editNumberFieldBeats({
                control,
                cursorKey,
                target,
                travelMs: TRAVEL_MS,
                hoverHoldMs: HOVER_HOLD_MS,
                moveCursor,
                clickCursor,
                setDemo: (c, phase) => setCfgDemo({ control: c, phase }),
                clearValue: () =>
                    patchDraft({ [key]: null } as Partial<ConfigDraft>),
                setValue: (value) =>
                    patchDraft({ [key]: value } as Partial<ConfigDraft>)
            }),
        [moveCursor, clickCursor, patchDraft]
    );

    // ── Policies beat factories (mirrors ProgramPoliciesSim) ─────────────
    const createPolicyBeats = useCallback(
        (): SimScriptStep[] => [
            {
                after: TRAVEL_MS,
                label: "pol-create:move",
                run: (): void => {
                    setCfgDemo(CFG_DEMO_IDLE);
                    moveCursor("pol-create-policy", { durationMs: TRAVEL_MS });
                }
            },
            {
                after: TRAVEL_MS,
                label: "pol-create:hover",
                run: () => setCreateHover("hover")
            },
            {
                after: HOVER_HOLD_MS,
                label: "pol-create:press",
                run: (): void => {
                    clickCursor();
                    setCreateHover("press");
                }
            }
        ],
        [moveCursor, clickCursor]
    );

    // A value FIELD inside the editor that TYPES its number in (Minimum
    // Threshold amount): travel → hover → click-to-focus (seed 0), then the
    // value builds digit-by-digit. `setValue` maps each typed prefix onto the
    // editor value.
    const typedEditorBeats = useCallback(
        (
            cursorKey: string,
            seedZero: () => void,
            target: number,
            setValue: (value: number) => void
        ): SimScriptStep[] => [
            {
                after: TRAVEL_MS,
                label: `pol-editor:${cursorKey}:move`,
                run: (): void => {
                    setCreateHover(null);
                    moveCursor(cursorKey, { durationMs: TRAVEL_MS });
                }
            },
            {
                after: TRAVEL_MS,
                label: `pol-editor:${cursorKey}:hover`,
                run: () =>
                    setPolDemo({ control: "config-value", phase: "hover" })
            },
            {
                after: HOVER_HOLD_MS,
                label: `pol-editor:${cursorKey}:focus`,
                run: (): void => {
                    clickCursor();
                    setPolDemo({ control: "config-value", phase: "press" });
                    seedZero();
                }
            },
            ...typedNumberBeats({
                target,
                startAfter: 220,
                label: `pol-editor:${cursorKey}`,
                setValue
            })
        ],
        [moveCursor, clickCursor]
    );

    // Build the Balance Amount Tranche ladder: the editor opens on ONE COMPLETE
    // base row (amount 0 · multiplier 1× — the "everyone at 1×" default). That
    // base row is NOT typed (it's already the sensible default), so the cursor
    // goes straight to +Add Tranche. For every APPENDED tier the cursor travels
    // to +Add Tranche and clicks it (a brief FLASH — the pressed visual paints
    // on the click beat, then clears on the next beat so it pulses like a real
    // click instead of sticking active) to append a fresh blank row, then
    // travels to that row's amount field → focuses it → TYPES it, then its
    // multiplier field → focuses it → TYPES it (decimals like 1.25 type their
    // fraction on a final beat). `tranches` is the FULL ladder (base first,
    // already at 0/1). The running ladder is re-serialized onto the editor value
    // on every beat so the rows render live as they fill.
    const trancheBuildBeats = useCallback(
        (
            tranches: { amount: number; multiplier: number }[]
        ): SimScriptStep[] => {
            // Mutable ladder shared across this sequence's run() closures. It's
            // re-seeded to the editor's opening single blank row at the start of
            // each replay (this factory runs once at memo-build, but the beats
            // replay each loop — so a play-time reset is required).
            let ladder: { amount: number | null; multiplier: number | null }[] =
                [];
            const beats: SimScriptStep[] = [];

            // A field (amount or multiplier) in `rowIndex`: travel to it → hover
            // → click-to-focus → TYPE its value digit-by-digit. `control` picks
            // which forced-visual seam paints ("tranche-amount" / "tranche-mult").
            const typeField = (
                rowIndex: number,
                control: "tranche-amount" | "tranche-mult",
                cursorKey: string,
                target: number,
                setValue: (value: number) => void,
                label: string
            ): SimScriptStep[] => [
                {
                    after: TRAVEL_MS,
                    label: `${label}:move`,
                    run: () => moveCursor(cursorKey, { durationMs: TRAVEL_MS })
                },
                {
                    after: TRAVEL_MS,
                    label: `${label}:hover`,
                    run: () =>
                        setPolDemo({
                            control,
                            phase: "hover",
                            trancheRow: rowIndex
                        })
                },
                {
                    after: HOVER_HOLD_MS,
                    label: `${label}:focus`,
                    run: (): void => {
                        clickCursor();
                        setPolDemo({
                            control,
                            phase: "press",
                            trancheRow: rowIndex
                        });
                    }
                },
                ...typedNumberBeats({
                    target,
                    startAfter: 220,
                    label,
                    setValue
                })
            ];

            const typeRow = (
                rowIndex: number,
                tier: { amount: number; multiplier: number },
                label: string
            ): SimScriptStep[] => [
                // Amount — cursor travels to this row's amount field, focuses,
                // TYPES its digits.
                ...typeField(
                    rowIndex,
                    "tranche-amount",
                    `pol-tranche-amount-${rowIndex}`,
                    tier.amount,
                    (value) => {
                        ladder[rowIndex] = {
                            ...ladder[rowIndex],
                            amount: value
                        };
                        patchPolicies({
                            editorValue: serializeTranches(ladder)
                        });
                    },
                    `${label}:amount`
                ),
                // Multiplier — cursor travels to this row's multiplier field,
                // focuses, TYPES its value (decimal like 1.25 lands its fraction
                // on the final beat).
                ...typeField(
                    rowIndex,
                    "tranche-mult",
                    `pol-tranche-mult-${rowIndex}`,
                    tier.multiplier,
                    (value) => {
                        ladder[rowIndex] = {
                            ...ladder[rowIndex],
                            multiplier: value
                        };
                        patchPolicies({
                            editorValue: serializeTranches(ladder)
                        });
                    },
                    `${label}:mult`
                )
            ];

            // Base row (index 0) — the editor's opening COMPLETE base row
            // (amount 0 · multiplier 1×, the "everyone at 1×" default). Re-seed
            // the ladder to that base at the top of each replay. It is NOT typed
            // (it's already the sensible default), so the cursor goes straight to
            // +Add Tranche for the higher tiers below.
            beats.push({
                after: 200,
                label: "tranche-base:seed",
                run: () => {
                    ladder = [
                        {
                            amount: tranches[0].amount,
                            multiplier: tranches[0].multiplier
                        }
                    ];
                    setPolDemo(POL_DEMO_IDLE);
                    patchPolicies({ editorValue: serializeTranches(ladder) });
                }
            });

            // Higher tiers — append a blank row via +Add Tranche, then type it.
            tranches.slice(1).forEach((tier, i) => {
                const rowIndex = i + 1;
                beats.push(
                    {
                        after: TRAVEL_MS,
                        label: `tranche-${i}:add-move`,
                        run: () => {
                            setPolDemo(POL_DEMO_IDLE);
                            moveCursor("pol-tranche-add", {
                                durationMs: TRAVEL_MS
                            });
                        }
                    },
                    {
                        after: TRAVEL_MS,
                        label: `tranche-${i}:add-hover`,
                        run: () =>
                            setPolDemo({
                                control: "tranche-add",
                                phase: "hover"
                            })
                    },
                    {
                        after: HOVER_HOLD_MS,
                        label: `tranche-${i}:add-press`,
                        run: () => {
                            clickCursor();
                            setPolDemo({
                                control: "tranche-add",
                                phase: "press"
                            });
                            // Append a new BLANK row; its amount + multiplier
                            // type in over the following beats.
                            ladder.push({ amount: null, multiplier: null });
                            patchPolicies({
                                editorValue: serializeTranches(ladder)
                            });
                        }
                    },
                    {
                        // FLASH: clear the +Add Tranche forced-press on the next
                        // beat so the button pulses (press → default) like a real
                        // click instead of staying active while the new row fills.
                        after: PRESS_HOLD_MS,
                        label: `tranche-${i}:add-release`,
                        run: () => setPolDemo(POL_DEMO_IDLE)
                    }
                );
                beats.push(...typeRow(rowIndex, tier, `tranche-${i}`));
            });
            return beats;
        },
        [moveCursor, clickCursor, patchPolicies]
    );

    // The Specific Access Control 3-way mode toggle (Whitelist / Blacklist /
    // KYC Only): travel to the target mode's ToggleMenuItem → hover → press +
    // commit the mode into the editor's wallet value, so the switch from the
    // default Whitelist onto KYC Only is VISIBLE before the policy is added.
    const modeToggleBeats = useCallback(
        (mode: string, commit: () => void): SimScriptStep[] => [
            {
                after: TRAVEL_MS,
                label: `mode-${mode}:move`,
                run: (): void => {
                    setCreateHover(null);
                    moveCursor(`pol-mode-${mode}`, { durationMs: TRAVEL_MS });
                }
            },
            {
                after: TRAVEL_MS,
                label: `mode-${mode}:hover`,
                run: () =>
                    setPolDemo({
                        control: "mode-toggle",
                        phase: "hover",
                        modeTarget: mode
                    })
            },
            {
                after: HOVER_HOLD_MS,
                label: `mode-${mode}:press`,
                run: (): void => {
                    clickCursor();
                    setPolDemo({
                        control: "mode-toggle",
                        phase: "press",
                        modeTarget: mode
                    });
                    commit();
                }
            }
        ],
        [moveCursor, clickCursor]
    );

    const addPolicyBeats = useCallback(
        (run: () => void): SimScriptStep[] => [
            {
                after: TRAVEL_MS,
                label: "pol-add:move",
                run: (): void => {
                    setCreateHover(null);
                    moveCursor("pol-add-policy", { durationMs: TRAVEL_MS });
                }
            },
            {
                after: TRAVEL_MS,
                label: "pol-add:hover",
                run: () => setPolDemo({ control: "add-policy", phase: "hover" })
            },
            {
                after: HOVER_HOLD_MS,
                label: "pol-add:press",
                run: (): void => {
                    clickCursor();
                    setPolDemo({ control: "add-policy", phase: "press" });
                    run();
                }
            }
        ],
        [moveCursor, clickCursor]
    );

    // Walk the policy-type Select down to `target` and pick it.
    const typeSelectBeats = useCallback(
        (target: ConfigurablePolicy): SimScriptStep[] => {
            const container = (): HTMLElement | null => containerRef.current;
            const targetIndex = POLICY_CATALOG.findIndex(
                (p) => p.id === target.id
            );
            const walk = POLICY_CATALOG.slice(0, targetIndex + 1);
            return [
                {
                    after: TRAVEL_MS,
                    label: "pol-type:move",
                    run: (): void => {
                        setCreateHover(null);
                        moveCursor("pol-type-select", {
                            durationMs: TRAVEL_MS
                        });
                    }
                },
                {
                    after: TRAVEL_MS,
                    label: "pol-type:hover",
                    run: () =>
                        setPolDemo({ control: "type-select", phase: "hover" })
                },
                {
                    after: HOVER_HOLD_MS,
                    label: "pol-type:open",
                    run: (): void => {
                        clickCursor();
                        setPolDemo({
                            control: "type-select",
                            phase: "press",
                            open: true,
                            portalContainer: container()
                        });
                    }
                },
                ...walk.flatMap((p, i): SimScriptStep[] => {
                    const optKey = `pol-type-opt-${p.id}`;
                    const travel =
                        i === 0 ? HOVER_HOLD_MS + 60 : OPTION_STEP_MS;
                    const stepMs = i === 0 ? TRAVEL_MS : OPTION_STEP_MS;
                    return [
                        {
                            after: travel,
                            label: `pol-type:travel-${p.id}`,
                            run: () =>
                                moveCursor(optKey, { durationMs: stepMs })
                        },
                        {
                            after: i === 0 ? TRAVEL_MS : OPTION_STEP_MS,
                            label: `pol-type:highlight-${p.id}`,
                            run: () =>
                                setPolDemo({
                                    control: "type-select",
                                    phase: "press",
                                    open: true,
                                    portalContainer: container(),
                                    highlightType: p.id
                                })
                        }
                    ];
                }),
                {
                    after: HOVER_HOLD_MS,
                    label: "pol-type:select",
                    run: (): void => {
                        clickCursor();
                        patchPolicies({
                            editorType: target,
                            editorValue: initialValueFor(
                                POLICY_CATALOG,
                                target.id
                            )
                        });
                        setPolDemo({
                            control: "type-select",
                            phase: "press",
                            open: false
                        });
                    }
                }
            ];
        },
        [moveCursor, clickCursor, containerRef, patchPolicies]
    );

    const steps = useMemo<readonly SimScriptStep[]>(
        () => [
            // ── Phase 1 — Configuration (Continuous) ─────────────────────
            // The draft OPENS on Continuous · Variable (earned) with the
            // fresh-draft defaults (Distribution 14) — so there's no Program
            // Type / Budget Type click beat; the Period row already reads
            // "Continuous", Budget Type reads Variable, and the Distribution
            // Schedule already reads 14 days (== the neobank's cadence == the
            // default, so it's NOT re-typed — that would be an invisible no-op).
            // The one visible config interaction is the non-default User %
            // split. The field OPENS at the real DEFAULT (100), and the cursor
            // EDITS it DOWN to the 80/20 split (80% to customers, 20% kept by the
            // neobank): focus → clear the 100 (blank) → type 8 → 80.
            ...editCfgBeats(
                "user-pct",
                "cfg-user-pct",
                "userPct",
                NB_A_CONFIG.userPct
            ),
            {
                after: 260,
                label: "cfg:settle",
                run: (): void => setCfgDemo(CFG_DEMO_IDLE)
            },

            // ── Phase 2 — Policies ───────────────────────────────────────
            // Policy 1 — Minimum Threshold 100 USDv (opens on the default type).
            ...createPolicyBeats(),
            {
                after: 260,
                label: "open-minimum-threshold",
                run: (): void => {
                    setCreateHover(null);
                    openEditor(
                        NB_MINIMUM_THRESHOLD,
                        initialValueFor(POLICY_CATALOG, NB_MINIMUM_THRESHOLD.id)
                    );
                }
            },
            // The Minimum Threshold field starts EMPTY; TYPE the 100 USDv floor
            // (1 → 10 → 100).
            ...typedEditorBeats(
                "pol-config-value",
                () => patchPolicies({ editorValue: "" }),
                NB_A_MINIMUM_THRESHOLD_VALUE,
                (value) => patchPolicies({ editorValue: value })
            ),
            ...addPolicyBeats(() =>
                commitEditor(NB_MINIMUM_THRESHOLD, NB_A_MINIMUM_THRESHOLD_VALUE)
            ),

            // Policy 2 — Balance Amount Tranche (walk the type select to it).
            ...createPolicyBeats(),
            {
                after: 260,
                label: "open-balance-tranche",
                run: (): void => {
                    setCreateHover(null);
                    openEditor(
                        NB_MINIMUM_THRESHOLD,
                        initialValueFor(POLICY_CATALOG, NB_MINIMUM_THRESHOLD.id)
                    );
                }
            },
            ...typeSelectBeats(NB_BALANCE_AMOUNT_TRANCHE),
            // Build the tranche ladder: the editor opens on ONE COMPLETE base
            // row (0 · 1× — the "everyone at 1×" default), which is NOT typed.
            // The cursor goes straight to +Add Tranche, adding the three higher
            // tiers one at a time, each row's amount + multiplier TYPED in
            // (>1,000 · 1.25× / >10,000 · 1.5× / >50,000 · 2×).
            ...trancheBuildBeats(NB_A_BALANCE_TRANCHES),
            ...addPolicyBeats(() =>
                commitEditor(
                    NB_BALANCE_AMOUNT_TRANCHE,
                    NB_A_BALANCE_TRANCHE_VALUE
                )
            ),

            // Policy 3 — Specific Access Control (KYC Only mode) → KYC gate.
            ...createPolicyBeats(),
            {
                after: 260,
                label: "open-wallet-list",
                run: (): void => {
                    setCreateHover(null);
                    openEditor(
                        NB_MINIMUM_THRESHOLD,
                        initialValueFor(POLICY_CATALOG, NB_MINIMUM_THRESHOLD.id)
                    );
                }
            },
            ...typeSelectBeats(NB_WALLET_LIST),
            // The editor opens on the DEFAULT Whitelist mode. Travel to + CLICK
            // "KYC Only" so the switch Whitelist → KYC Only is visible; the
            // editor value flips to the KYC gate (manual address list hidden).
            ...modeToggleBeats("kyc-only", () =>
                patchPolicies({
                    editorValue: serializeWalletConfig({
                        mode: "kyc-only",
                        addresses: []
                    })
                })
            ),
            ...addPolicyBeats(() =>
                commitEditor(
                    NB_WALLET_LIST,
                    // KYC Only mode — this is the neobank's KYC gate for the
                    // balance-rewards program (only KYC-approved wallets qualify).
                    serializeWalletConfig({
                        mode: "kyc-only",
                        addresses: []
                    })
                )
            ),
            {
                after: 260,
                label: "pol:settle",
                run: (): void => {
                    setPolDemo(POL_DEMO_IDLE);
                    setCreateHover(null);
                }
            },

            // ── Phase 3 — Activation ─────────────────────────────────────
            {
                after: TRAVEL_MS + 120,
                label: "activate:move",
                run: (): void =>
                    moveCursor("nb-a-add-program", { durationMs: TRAVEL_MS })
            },
            {
                after: TRAVEL_MS,
                label: "activate:hover",
                run: (): void => setCtaDemo("hover")
            },
            {
                after: HOVER_HOLD_MS,
                label: "activate:press",
                run: (): void => {
                    clickCursor();
                    setCtaDemo("press");
                }
            },
            // After the click the ENTIRE sim (builder + cursor) FADES OUT to
            // opacity 0, holds faded for ~3s, then the loop reset fades it back
            // in and replays from the empty draft.
            {
                after: PRESS_HOLD_MS,
                label: "activate:fade-out",
                run: (): void => {
                    setCtaDemo(null);
                    hideCursor();
                    setFaded(true);
                }
            },
            {
                after: FADE_MS + FADE_HOLD_MS,
                label: "activate:fade-hold",
                run: (): void => {}
            }
        ],
        [
            editCfgBeats,
            createPolicyBeats,
            typedEditorBeats,
            trancheBuildBeats,
            modeToggleBeats,
            addPolicyBeats,
            typeSelectBeats,
            openEditor,
            commitEditor,
            patchPolicies,
            moveCursor,
            clickCursor,
            hideCursor
        ]
    );

    useSimScript({ reset, steps, loopPauseMs });

    const { attached, editorType, editorValue } = policies;
    const editorOpen = editorType !== null;

    const policyAction = (
        <Button
            variant="sunken"
            size="sm"
            disabled={editorOpen}
            data-sim-cursor-key="pol-create-policy"
            forceHover={createHover === "hover"}
            forcePressed={createHover === "press"}
        >
            +Create Policy
        </Button>
    );

    return (
        // Fade wrapper — after Add Program `faded` flips true → opacity 0; the
        // loop reset flips it back to false → opacity 1. `transition-opacity
        // duration-500` (Tailwind defaults) animates both directions.
        <div
            ref={cursor.containerRef}
            className={`relative transition-opacity duration-500 ${
                faded ? "opacity-0" : "opacity-100"
            }`}
        >
            {/* Draft header */}
            <section className="py-4">
                <div className="px-4 flex items-center gap-8">
                    <Heading
                        title={NB_A_PROGRAM_NAME}
                        titleVariant="h2"
                        tone="base"
                        titleClassName="text-3xl"
                    />
                    <StatusBadge
                        state="pending"
                        label="Draft"
                        labelClassName="text-warning-base"
                    />
                </div>
            </section>

            {/* Configuration (Continuous) */}
            <ProgramConfiguration
                program={CONTINUOUS_PROGRAM_FIXTURE}
                demo={cfgDemo}
                programType={draft.programType}
                setProgramType={(programType) => patchDraft({ programType })}
                budgetMode={draft.budgetMode}
                setBudgetMode={(budgetMode) => patchDraft({ budgetMode })}
                apyMode={draft.apyMode}
                setApyMode={(apyMode) => patchDraft({ apyMode })}
                baseApy={draft.baseApy}
                setBaseApy={(baseApy) => patchDraft({ baseApy })}
                userPct={draft.userPct}
                setUserPct={(userPct) => patchDraft({ userPct })}
                periodDays={draft.periodDays}
                setPeriodDays={(periodDays) => patchDraft({ periodDays })}
                settlementCycleLength={draft.settlementCycleLength}
                setSettlementCycleLength={(settlementCycleLength) =>
                    patchDraft({ settlementCycleLength })
                }
                settlementCycleUnit={draft.settlementCycleUnit}
                setSettlementCycleUnit={(settlementCycleUnit) =>
                    patchDraft({ settlementCycleUnit })
                }
                budget={draft.budget}
                setBudget={(budget) => patchDraft({ budget })}
            />

            {/* Policies */}
            <Section
                heading="Policies"
                className="pt-8"
                headerClassName="px-4 mb-6"
                action={policyAction}
            >
                {editorOpen && editorType && (
                    <div className="px-4 mb-4">
                        <PolicyCreator
                            available={POLICY_CATALOG}
                            controlledPolicyId={editorType.id}
                            controlledValue={editorValue}
                            onControlledValueChange={(v) =>
                                patchPolicies({ editorValue: v })
                            }
                            demo={polDemo}
                            onAdd={() => {}}
                            onCancel={() => {}}
                        />
                    </div>
                )}
                {attached.length > 0 && (
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="text-foreground-muted block mt-6 mb-1 px-4"
                    >
                        Added
                    </Text>
                )}
                <List>
                    {attached.map(({ policy, value }) => (
                        <PolicyRow
                            key={policy.id}
                            policy={policy}
                            value={value}
                            onRemove={() => {}}
                        />
                    ))}
                </List>
            </Section>

            {/* Add Program CTA */}
            <section className="mt-8 mb-10 px-4 flex items-center gap-3">
                <FormActions
                    size="lg"
                    primaryLabel="Add Program"
                    onPrimary={() => {}}
                    cancelLabel="Cancel"
                    onCancel={() => {}}
                    primaryCursorKey="nb-a-add-program"
                    primaryForceHover={ctaDemo === "hover"}
                    primaryForcePressed={ctaDemo === "press"}
                />
            </section>
            {cursor.layer}
        </div>
    );
}

/**
 * Neobank "Continuous Balance Rewards" walkthrough. Add `framed` to wrap it in
 * its own fixed-height {@link SimFrame} (the block owns its max height); omit
 * it to render bare.
 */
export const NeobankContinuousProgramSim = framable(
    NeobankContinuousProgramSimView,
    "NeobankContinuousProgramSim"
);
